import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { listConversations } from '@/lib/messaging/messaging-client';
import { useAuth } from '@/contexts/AuthContext';

const FOCUSED_INTERVAL_MS = 30_000;
const HIDDEN_INTERVAL_MS = 120_000;

export const conversationsQueryKey = ['messaging', 'conversations'] as const;

export const useConversations = () => {
  const { profile } = useAuth();
  const profileId = profile?.id;

  const [hidden, setHidden] = useState<boolean>(
    typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false,
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVis = () => setHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const refetchInterval = hidden ? HIDDEN_INTERVAL_MS : FOCUSED_INTERVAL_MS;

  const query = useQuery({
    queryKey: conversationsQueryKey,
    enabled: !!profileId,
    refetchInterval,
    queryFn: async () => {
      const { data, error } = await listConversations();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  const activeConversations = useMemo(
    () => (query.data ?? []).filter((c) => !c.archived_at),
    [query.data],
  );
  const archivedConversations = useMemo(
    () => (query.data ?? []).filter((c) => !!c.archived_at),
    [query.data],
  );

  return {
    ...query,
    activeConversations,
    archivedConversations,
  };
};
